import {
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Tag,
  message,
} from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  Outlet,
  useActionData,
  useBlocker,
  useLoaderData,
  useSubmit,
} from "react-router-dom";
import {
  assignTemplateLabel,
  getEmailTemplate,
  getTemplateLabels,
  unassignTemplateLabel,
  updateEmailTemplate,
} from "../../api/settings/email-template";
import TemplateLabelDrawer from "./template-label-drawer";
import GrapesJSEditor from "../../components/inputs/email-editor.jsx";
import Page from "../../components/page/page";
import { generateRouteUrl } from "../../library/constants/index.js";
import {
  cmsLanguageName,
  supportedLanguages,
} from "../../library/helpers/language.js";

const EmailTemplateDetails = ({ title }) => {
  const { emailTemplate } = useLoaderData();
  useActionData();
  const submit = useSubmit();
  const [isActive, setIsActive] = useState(!!emailTemplate.active);
  const [subject, setSubject] = useState(emailTemplate.subject);
  const [description, setDescription] = useState(
    emailTemplate.description || "",
  );
  const [labels, setLabels] = useState(emailTemplate.labels || []);
  const [allLabels, setAllLabels] = useState([]);
  const [labelDrawerOpen, setLabelDrawerOpen] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    getTemplateLabels().then(setAllLabels).catch(() => {});
  }, []);

  // Block navigating elsewhere when data has been entered into the input
  let blocker = useBlocker(({ currentLocation, nextLocation }) => {
    console.log({
      dirty: editorRef.current.isDirty(),
    });
    return (
      currentLocation.pathname !== nextLocation.pathname &&
      editorRef.current.isDirty()
    );
  });

  useEffect(() => {
    if (blocker.state === "blocked") {
      Modal.confirm({
        title: "Confirmation Required",
        content:
          "Any unsaved changes will be lost if you proceed. Do you want to continue?",
        onOk() {
          blocker.proceed();
        },
        onCancel() {
          blocker.reset();
        },
      });
    }
  }, [blocker.state]);

  useEffect(() => {
    setSubject(emailTemplate.subject);
    setIsActive(!!emailTemplate.active);
    setDescription(emailTemplate.description || "");
    setLabels(emailTemplate.labels || []);
  }, [emailTemplate]);

  const handleSubjectChange = (event) => {
    setSubject(event.target.value);
  };

  const handleActiveChagne = (checked) => {
    setIsActive(!!checked);
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const handleAssignLabel = async (label) => {
    try {
      await assignTemplateLabel(emailTemplate.id, label.id);
      setLabels((prev) => [...prev, label]);
    } catch {
      message.error("Failed to assign label");
    }
  };

  const handleUnassignLabel = async (labelId) => {
    try {
      await unassignTemplateLabel(emailTemplate.id, labelId);
      setLabels((prev) => prev.filter((l) => l.id !== labelId));
    } catch {
      message.error("Failed to remove label");
    }
  };

  const handleSave = () => {
    const { html, css } = editorRef.current.getHtmlAndCss();
    submit(
      {
        subject: subject,
        active: isActive,
        content: html,
        description: description,
        css: css,
        action: "update",
      },
      {
        replace: true,
        method: "POST",
        encType: "application/json",
      },
    );
  };

  return (
    <Page title={title}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        {/* Left: title + optional language switcher */}
        <div>
          <h2 style={{ margin: 0 }}>{emailTemplate.name}</h2>
          {emailTemplate.multilingual && (
            <Space size="small" wrap style={{ marginTop: 8 }}>
              {supportedLanguages.map((language) =>
                language === emailTemplate.language ? (
                  <Button key={language} size="small" type="primary">
                    {cmsLanguageName(language)}
                  </Button>
                ) : (
                  <Link
                    key={language}
                    to={generateRouteUrl("EmailTemplateDetails", {
                      id: emailTemplate.id,
                      language,
                    })}
                    replace
                  >
                    <Button size="small">{cmsLanguageName(language)}</Button>
                  </Link>
                ),
              )}
            </Space>
          )}
        </div>

        {/* Right: Labels + Active */}
        <Space align="start" size={24}>
          <div>
            <div style={{ fontSize: 14, color: "rgba(0,0,0,0.88)", fontWeight: 600, marginBottom: 4 }}>
              Labels
            </div>
            <Space size={4} wrap>
              {labels.map((label) => (
                <Tag
                  key={label.id}
                  color={label.color}
                  closable
                  onClose={(e) => { e.preventDefault(); handleUnassignLabel(label.id); }}
                  style={{ borderRadius: 5, margin: 0, padding: "4px 8px", lineHeight: 1 }}
                >
                  {label.name}
                </Tag>
              ))}
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    ...allLabels
                      .filter((l) => !labels.some((a) => a.id === l.id))
                      .map((l) => ({
                        key: l.id,
                        label: (
                          <Tag color={l.color} style={{ borderRadius: 5, margin: 0, padding: "4px 8px", lineHeight: 1 }}>
                            {l.name}
                          </Tag>
                        ),
                        onClick: () => handleAssignLabel(l),
                      })),
                    { type: "divider" },
                    {
                      key: "manage",
                      label: <span style={{ color: "#23b17c", fontSize: 13 }}>Manage labels</span>,
                      onClick: () => setLabelDrawerOpen(true),
                    },
                  ],
                }}
              >
                <Tag
                  style={{
                    borderStyle: "dashed",
                    cursor: "pointer",
                    borderRadius: 5,
                    margin: 0,
                    padding: "4px 8px",
                    lineHeight: 1,
                  }}
                >
                  +
                </Tag>
              </Dropdown>
            </Space>
          </div>
          <div>
            <div style={{ fontSize: 14, color: "rgba(0,0,0,0.88)", fontWeight: 600, marginBottom: 4 }}>
              Active
            </div>
            <Switch checked={isActive} onChange={handleActiveChagne} />
          </div>
        </Space>

        <TemplateLabelDrawer
          open={labelDrawerOpen}
          onClose={() => setLabelDrawerOpen(false)}
          onLabelsChange={(updater) => {
            setAllLabels((prev) => {
              const next = typeof updater === "function" ? updater(prev) : updater;
              setLabels((assigned) => assigned.filter((l) => next.some((a) => a.id === l.id)));
              return next;
            });
          }}
        />
      </div>

      <Form layout="vertical" style={{ marginTop: "32px" }}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Subject">
              <Input value={subject} onChange={handleSubjectChange} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Scenario">
              <Input.TextArea
                value={description}
                onChange={handleDescriptionChange}
                rows={1}
                autoSize={{ minRows: 1, maxRows: 4 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Content">
          <GrapesJSEditor
            ref={editorRef}
            content={emailTemplate.content}
            cssStyles={emailTemplate.css}
            sample={emailTemplate.sample}
            header={!["header", "footer"].includes(emailTemplate.name)}
            footer={!["header", "footer"].includes(emailTemplate.name)}
          />
        </Form.Item>
        <div className="actions align--left">
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
          <Link to={-1}>
            <Button className="cancelButton">Cancel</Button>
          </Link>
        </div>
      </Form>
      <Outlet />
    </Page>
  );
};

EmailTemplateDetails.Actions = {
  update: async ({ params, data }) => {
    const { id, language } = params;
    try {
      const msg = await updateEmailTemplate(id, language, data);
      message.success(msg);
      return true;
    } catch (error) {
      message.error(error.message);
      return { errors: error.error };
    }
  },
};

EmailTemplateDetails.Loader = async ({ params }) => {
  const { id, language } = params;
  const emailTemplate = await getEmailTemplate(id, language);
  return { emailTemplate };
};

EmailTemplateDetails.propTypes = {
  title: PropTypes.string,
};

export default EmailTemplateDetails;
